<script setup lang="ts">
import type { IWidgetUser } from './types'
import { UserRound, Mail, Phone, Smartphone, Globe } from 'lucide-vue-next'

const props = withDefaults(defineProps<IWidgetUser>(), {
  variant: 'white',
  hideContacts: false,
  showNews: false
})
</script>

<template>
  <div
    class="relative flex flex-col justify-between rounded-lg p-4"
    :class="props.variant === 'white' ? 'bg-white' : 'bg-gray-100'"
  >
    <div v-if="props.index" class="absolute right-4 top-4">
      <div
        v-if="props.index === 1"
        class="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 font-bold text-white shadow-md"
      >
        1
      </div>
      <div
        v-else-if="props.index === 2"
        class="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-gray-300 to-gray-400 font-bold text-white shadow-md"
      >
        2
      </div>
      <div
        v-else-if="props.index === 3"
        class="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-amber-600 to-amber-700 font-bold text-white shadow-md"
      >
        3
      </div>
    </div>
    <div class="flex items-center gap-2">
      <NuxtImg
        v-if="props.avatar"
        :src="props.avatar"
        :alt="props.fullname"
        width="96"
        height="96"
        size="96px"
        class="mr-4 h-24 w-24 rounded-[16px]"
      />
      <div
        v-else
        class="mr-4 flex h-24 w-24 shrink-0 items-center justify-center rounded-[16px] bg-gray-100"
      >
        <UserRound />
      </div>
      <div class="flex flex-col gap-0.5">
        <NuxtLinkLocale
          v-if="props.url"
          :to="props.url"
          class="font-serif font-bold leading-4 hover:underline"
          >{{ props.fullname }}</NuxtLinkLocale
        >
        <div v-else class="font-serif font-bold leading-4">{{ props.fullname }}</div>
        <div v-if="props.description" class="mt-4 text-sm leading-3 text-gray-700">
          {{ props.description }}
        </div>
        <div v-if="props.showNews" class="mt-4 text-xs">
          {{ $t('common.publications_count') }} {{ props.news_count || 0 }}
        </div>
      </div>
    </div>

    <div
      v-if="props.contacts?.length && !hideContacts"
      class="ml-28 mt-4 flex flex-wrap gap-4 pl-2"
    >
      <template v-for="(contact, index) in props.contacts" :key="index">
        <a
          v-if="'email' in contact"
          :href="`mailto:${contact.email}`"
          target="_blank"
          :aria-label="$t('common.send.email.to', { email: contact.email })"
          class="text-blue-600 hover:text-blue-700"
        >
          <Mail class="h-4 w-4" />
        </a>

        <a
          v-else-if="'telegram' in contact"
          :href="`https://t.me/${contact.telegram}`"
          target="_blank"
          :aria-label="$t('common.open.telegram.chat', { username: contact.telegram })"
          class="text-blue-600 hover:text-blue-700"
        >
          <i class="[&>svg]:h-4 [&>svg]:w-4"><UiIcon name="telegram" /></i>
        </a>

        <a
          v-else-if="'whatsapp' in contact"
          :href="`https://wa.me/${contact.whatsapp}`"
          target="_blank"
          :aria-label="$t('common.open.whatsapp.chat', { phone: contact.whatsapp })"
          class="text-blue-600 hover:text-blue-700"
        >
          <i class="[&>svg]:h-4 [&>svg]:w-4"><UiIcon name="whatsapp" /></i>
        </a>

        <a
          v-else-if="'mobile_phone' in contact || 'office_phone' in contact"
          :href="`tel:${contact.mobile_phone || contact.office_phone}`"
          :aria-label="
            $t('common.call.phone', { phone: contact.mobile_phone || contact.office_phone })
          "
          class="text-blue-600 hover:text-blue-700"
        >
          <Smartphone v-if="contact.mobile_phone" class="h-4 w-4" />
          <Phone v-else class="h-4 w-4" />
        </a>

        <a
          v-else-if="'website_url' in contact"
          :href="contact.website_url"
          target="_blank"
          rel="noopener noreferrer"
          :aria-label="$t('common.open.website', { website: contact.website_url })"
          class="text-blue-600 hover:text-blue-700"
        >
          <Globe class="h-4 w-4" />
        </a>


      </template>
    </div>
  </div>
</template>
